import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { Visibility, Delete } from '@mui/icons-material';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';

import CompanyModal from './CompanyModal';
import { supabase } from '../../services/supabase';
import { useNotification } from '../../contexts/NotificationContext';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import DataTable from '../shared/DataTable';
import { formatDateTime } from '../../utils/dateUtils';
import { errorMessageOf } from '../../utils/errorMessage';
import { Company as CompanyDetails } from '../../types/company.types';

interface Company {
  id: string;
  name: string;
  openai_api_key: string | null;
  openrouter_api_key: string | null;
  created_at: string;
  updated_at: string;
}

const CompaniesTab = () => {
  const { t } = useTranslation();
  const { isSuperAdmin } = usePermissions();
  const { companies, isLoading, triggerCompanyFetch } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<string | null>(null);

  const handleCreateCompany = async (companyData: Partial<Company>) => {
    try {
      const { error } = await supabase.from('companies').insert([companyData]).select().single();

      if (error) throw error;
      showNotification(t('company.company_created_successfully'), 'success');
      await triggerCompanyFetch();
    } catch (error) {
      console.error('Error creating company:', error);
      const detail = errorMessageOf(error);
      showNotification(
        detail ? `${t('company.error_creating_company')}: ${detail}` : t('company.error_creating_company'),
        'error'
      );
      Sentry.captureException(error);
    }
  };

  const handleUpdateCompany = async (companyData: Partial<Company>) => {
    if (!selectedCompany) return;

    try {
      const { error } = await supabase
        .from('companies')
        .update(companyData)
        .eq('id', selectedCompany.id)
        .select()
        .single();

      if (error) throw error;
      showNotification(t('company.company_updated_successfully'), 'success');
      await triggerCompanyFetch();
    } catch (error) {
      console.error('Error updating company:', error);
      const detail = errorMessageOf(error);
      showNotification(
        detail ? `${t('company.error_updating_company')}: ${detail}` : t('company.error_updating_company'),
        'error'
      );
      Sentry.captureException(error);
    }
  };

  const handleDeleteClick = (id: string) => {
    setCompanyToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;

    try {
      const { error } = await supabase.from('companies').delete().eq('id', companyToDelete);

      if (error) throw error;
      showNotification(t('company.company_deleted_successfully'), 'success');
      await triggerCompanyFetch();
    } catch (error) {
      console.error('Error deleting company:', error);
      showNotification(t('company.error_deleting_company'), 'error');
      Sentry.captureException(error);
    } finally {
      setIsDeleteModalOpen(false);
      setCompanyToDelete(null);
    }
  };

  const openModal = (company?: Company) => {
    if (company) {
      navigate(`/company/${company.id}`);
    } else {
      setSelectedCompany(undefined);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setSelectedCompany(undefined);
    setIsModalOpen(false);
  };

  const columnDefs: ColDef<Company>[] = [
    {
      field: 'name',
      headerName: t('company.company_name'),
      sortable: true,
      filter: true,
      onCellClicked: (params) => {
        openModal(params.data);
      },
      cellStyle: {
        cursor: 'pointer',
        color: '#3b82f6', // blue-500 color in Tailwind
      },
    },
    ...(isSuperAdmin()
      ? ([
          {
            field: 'openrouter_api_key',
            headerName: t('company.openrouter_api_key'),
            sortable: true,
            filter: true,
            valueFormatter: (params: ValueFormatterParams<Company>) => {
              return params.value ? `${params.value.slice(0, 8)}...` : '';
            },
          },
        ] as ColDef<Company>[])
      : []),
    {
      field: 'created_at',
      headerName: t('company.created_at'),
      sortable: true,
      filter: true,
      valueFormatter: (params: ValueFormatterParams<Company>) => {
        return params.value ? formatDateTime(params.value) : '-';
      },
    },
    {
      headerName: t('company.actions'),
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<Company>) => {
        return (
          <div className="flex space-x-2">
            <button
              onClick={() => openModal(params.data)}
              className="text-blue-600 hover:text-blue-900"
              title={t('company.view_company')}
            >
              <Visibility className="h-5 w-5" />
            </button>
            {isSuperAdmin() && (
              <button
                onClick={() => handleDeleteClick(params.data?.id as string)}
                className="text-red-600 hover:text-red-900"
                title={t('company.delete_company')}
              >
                <Delete className="h-5 w-5" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const gridConfig = {
    pagination: true,
    // paginationPageSize: 50,
    paginationAutoPageSize: true,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      resizable: true,
    },
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4 flex-shrink-0">
        <h3 className="text-xl font-semibold">{t('company.companies')}</h3>
        {isSuperAdmin() && (
          <button
            onClick={() => openModal()}
            className="px-2 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {t('company.add_company')}
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow flex-grow min-h-0">
        <DataTable<Company>
          rowData={companies}
          autoHeight={false}
          height={'100%'}
          columnDefs={columnDefs}
          gridConfig={gridConfig}
          isLoading={isLoading}
          theme="ag-theme-alpine"
          noRowsMessage={t('company.no_companies')}
        />
      </div>

      <CompanyModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={selectedCompany ? handleUpdateCompany : handleCreateCompany}
        company={selectedCompany as unknown as CompanyDetails}
      />

      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteCompany}
        title={t('company.delete_company')}
        message={t('company.delete_company_message')}
      />
    </div>
  );
};

export default CompaniesTab;
